/**
 * The patient search endpoint, as the frontend sees it.
 *
 * Mirrors `api/src/Continuum.Api/Patients/PatientSearchResults.cs`. Values and codes,
 * never rendered text: the birth date is a calendar date and goes through a shared
 * formatter on the way to the screen (I18N.md §3), the allergy status is a code the
 * catalogue turns into Swedish, and the isolation type is record content and is shown
 * as written (AGENTS.md §7, §3 rule 8).
 */

import { unauthenticated } from '../auth/sessionExpiry'
import type {
  AllergyStatus,
  IdentifierSystem,
  PatientBirthDate,
  PatientIdentifier,
  PatientNames,
  PatientSafety,
} from './patientRecord'

/**
 * The shortest term the endpoint will search on.
 *
 * `PatientSearchTerm.MinimumLength` on the server, repeated here because the screen
 * has to know it before it asks: below three characters `pg_trgm` has no trigram to
 * look up and the search degenerates into a full scan — once per keystroke, each with
 * an audit row that can never be deleted. **The server's limit is the one that binds**
 * (it returns `PATIENT_SEARCH_TERM_TOO_SHORT` regardless); this one exists so the
 * first two keystrokes never leave the browser.
 */
export const SEARCH_MINIMUM_LENGTH = 3

/**
 * The longest term the endpoint accepts — `PatientSearchTerm.MaximumLength`.
 *
 * Used to render the message, not to truncate. Truncating in the field would search
 * for something other than what a clinician typed while showing them their own text.
 */
export const SEARCH_MAXIMUM_LENGTH = 64

/**
 * How long the field waits after the last keystroke before it asks.
 *
 * §2.3 asks for hits *while typing*, which is a requirement about what the clinician
 * experiences and not about how many requests are sent. 250 ms is below the threshold
 * where a list feels like it lags behind the keyboard, and it collapses a typed
 * personnummer — twelve keystrokes — into one search and therefore one audit row
 * instead of ten.
 */
export const SEARCH_DEBOUNCE_MS = 250

export interface PatientSearchHit
  extends PatientNames,
    PatientIdentifier,
    PatientBirthDate,
    PatientSafety {
  /** `clinical.patient.record_id` — the stable logical identity. */
  readonly patientId: string

  readonly identifierSystem: IdentifierSystem | null

  /** Ward code of the current admission, or null when the patient is not admitted. */
  readonly ward: string | null

  /** Bed label of the current admission, or null. */
  readonly bed: string | null

  readonly allergyStatus: AllergyStatus
}

export interface PatientSearchResults {
  readonly hits: readonly PatientSearchHit[]

  /** How many hits the endpoint returns at most. Rendered, so it is read from here. */
  readonly limit: number

  /**
   * There were more matches than `limit`.
   *
   * Said out loud by the server rather than inferred from `hits.length === limit`,
   * and shown on the screen: a clinician who cannot see that the list is cut short
   * will read "not in the list" as "not in the record", which during an outage is the
   * wrong conclusion about a patient standing in front of them.
   */
  readonly truncated: boolean
}

/**
 * Why a search did not produce a result. Codes, so the catalogue does the words.
 *
 * `too-short` is not an error state on the screen — it is the ordinary state of an
 * empty field — but it is a distinct answer from `empty`, and keeping them apart is
 * the difference between "keep typing" and "this patient is not in the record".
 */
export type PatientSearchFailure = 'too-short' | 'too-long' | 'unavailable'

export class PatientSearchError extends Error {
  readonly failure: PatientSearchFailure

  constructor(failure: PatientSearchFailure) {
    super(`Patient search failed: ${failure}`)
    this.name = 'PatientSearchError'
    this.failure = failure
  }
}

/** The error body the API returns: a code and params, never a sentence (I18N.md §1). */
interface ApiError {
  readonly error?: { readonly code?: string }
}

/**
 * Searches for patients.
 *
 * One request over names, personnummer and reservnummer at once — there is
 * deliberately nothing here that decides which of the three a term looks like, for
 * the reason `PatientSearchTerm` gives: a classifier is the thing that gets
 * reservnummer wrong.
 *
 * **The term goes in the query string, and that is deliberate.** It is what the audit
 * row records about the search (`AuditedAccessMetadata.DetailQueryKeys`), read before
 * the handler so the row names what was asked even when the search then failed.
 */
export async function searchPatients(
  term: string,
  signal: AbortSignal,
): Promise<PatientSearchResults> {
  let response: Response

  try {
    response = await fetch(`/api/patients?q=${encodeURIComponent(term)}`, {
      signal,
      headers: { Accept: 'application/json' },
    })
  } catch (cause) {
    // An aborted fetch is the next keystroke, not a failure. Re-thrown so the caller
    // can tell the two apart rather than showing an error for a search nobody is
    // waiting for any more.
    if (signal.aborted) {
      throw cause
    }
    throw new PatientSearchError('unavailable')
  }

  if (response.status === 400) {
    // The two length codes are separate answers and are read, not guessed from the
    // length of the term: the server's bounds are the ones that bind.
    const body = (await response.json().catch(() => ({}))) as ApiError

    throw new PatientSearchError(
      body.error?.code === 'PATIENT_SEARCH_TERM_TOO_LONG' ? 'too-long' : 'too-short',
    )
  }

  // Before the general case, because a 401 is not an unavailable box: it means the session
  // has ended, and `SessionGate` is about to show the login screen. Reported rather than
  // rendered — see `auth/sessionExpiry.ts`. The throw still happens, so this screen does not
  // sit half-updated in the moment before it unmounts.
  unauthenticated(response)

  if (!response.ok) {
    throw new PatientSearchError('unavailable')
  }

  return (await response.json()) as PatientSearchResults
}
